<template>
  <v-container ref="container" v-scroll="onScroll">
    <TitleParagraphRow :value="value" />

    <v-row
      v-if="isMounted && !hideFilters"
      :style="{ top: `${stickyTop}px` }"
      class="sticky-lg"
      style="z-index: 1"
    >
      <v-col v-if="filtersData">
        <v-card elevation="4" rounded="0" theme="light">
          <v-card-text>
            <v-row align="center">
              <v-col>
                <v-card
                  class="text-h4 font-weight-light"
                  variant="flat"
                  @click="toggleFilters"
                >
                  <v-icon
                    :icon="mdiTuneVariant"
                    color="surface-variant"
                    size="42"
                  />
                  Filters</v-card
                ></v-col
              >

              <v-col cols="auto">
                <ShareLinkButton class="mr-1" variant="text" />
                <v-btn variant="text" @click="resetFilters"
                  >Reset Filters</v-btn
                >
                <v-btn
                  :append-icon="filtersShown ? mdiChevronUp : mdiChevronDown"
                  variant="text"
                  @click="toggleFilters"
                  >{{ filtersShown ? 'Hide' : 'Show' }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>

          <v-expand-transition>
            <v-card-text v-if="filtersShown">
              <v-row>
                <ResourcesFacet
                  v-if="shownFilters.topic"
                  v-model="filtersData.topics"
                  :facet-counts="facetCounts"
                  :items="topicItems"
                  facet-name="Topic"
                >
                  <template #title> Filter by Topic </template>
                </ResourcesFacet>

                <ResourcesFacet
                  v-if="shownFilters.course"
                  v-model="filtersData.courses"
                  :facet-counts="facetCounts"
                  :items="courseItems"
                  facet-name="Course"
                >
                  <template #title> Filter by Course </template>
                </ResourcesFacet>
              </v-row>

              <v-row v-if="shownFilters.type">
                <v-col>
                  <v-chip-group
                    v-model="filtersData.type"
                    mandatory
                    selected-class="text-red-darken-2"
                  >
                    <v-chip
                      v-for="item in typeItems"
                      :key="item.id"
                      :value="item.id"
                    >
                      {{ item.name }} ({{
                        get(facetCounts, `Type.${item.id}`, 0)
                      }})
                    </v-chip>
                  </v-chip-group>
                </v-col>
              </v-row>
            </v-card-text>
          </v-expand-transition>
        </v-card>
      </v-col>
    </v-row>

    <ResourceList
      :facet-keys="facetKeys"
      :limit="!isMounted ? undefined : 18"
      :value="data"
      show-all-button
    />
  </v-container>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { contentProvider } from '#root/renderer/contentProvider'
import { usePageContext } from '#root/renderer/usePageContext'
import { useMounted } from '@vueuse/core'
import { MENU_BAR_HEIGHT, FILTER_TYPE_ALL } from '#root/lib/consts'
import { scrollToRef } from '#root/lib/utils'
import { FacetIndexer } from '#root/lib/facet-indexer'
import get from 'lodash.get'
import setWith from 'lodash.setwith'
import debounce from 'lodash.debounce'
import { mdiChevronDown, mdiChevronUp, mdiTuneVariant } from '@mdi/js'

const FILTERS_DEFAULT = {
  course: true,
  topic: true,
  type: true
}
const FILTERS_VALUE_DEFAULT = {
  courses: [],
  topics: [],
  type: FILTER_TYPE_ALL
}

const emit = defineEmits(['update:filtersValue'])

const props = defineProps({
  hideFilters: {
    default: false,
    type: Boolean
  },
  filters: {
    default: undefined,
    type: Object
  },
  filtersValue: {
    default: undefined,
    type: Object
  },
  stickyTop: {
    default: MENU_BAR_HEIGHT,
    type: Number
  },
  value: {
    required: true,
    type: Object
  }
})

const { lgAndUp } = useDisplay()
const { courses } = usePageContext()
const isMounted = useMounted()
const container = ref(null)
const filtersData = ref(null)
const filtersShown = ref(false)
const filtersAutoHideEnabled = ref(true)
const data = ref(null)
const indexer = ref(null)
const courseItems = computed(() => {
  return (
    (indexer.value &&
      courses &&
      courses.filter(({ id }) => indexer.value.facets.Course[id + ''])) ||
    []
  )
})
const topics = ref(null)
const topicItems = computed(() => {
  return (
    (indexer.value &&
      topics.value &&
      topics.value.filter(
        ({ id, position }) =>
          position > -1 && indexer.value.facets.Topic[id + '']
      )) ||
    []
  )
})
const types = ref(null)
const typeItems = computed(() => {
  return [
    {
      id: FILTER_TYPE_ALL,
      name: 'See All'
    },
    ...(!(indexer.value && types.value)
      ? []
      : types.value.filter(
          ({ id, position }) =>
            position > -1 && indexer.value.facets.Type[id + '']
        ))
  ]
})
const facetCounts = ref(null)
const facetKeys = ref(null)
const facetQuery = computed(() => {
  const query = {}

  if (filtersData.value) {
    if (filtersData.value.courses)
      filtersData.value.courses.forEach(id => {
        setWith(query, `Course.${id}`, true, Object)
      })
    if (filtersData.value.topics)
      filtersData.value.topics.forEach(id => {
        setWith(query, `Topic.${id}`, true, Object)
      })
    if (filtersData.value.type)
      setWith(query, `Type.${filtersData.value.type}`, true, Object)
  }

  return query
})
const shownFilters = computed(() => {
  return Object.assign({}, FILTERS_DEFAULT, props.filters)
})
let debounced

async function fetchData() {
  await Promise.all([
    contentProvider.findOne('resources', '', true).then(d => (data.value = d)),
    contentProvider
      .findMany('resource-topics', true)
      .then(d => (topics.value = d)),
    contentProvider
      .findMany('resource-types', true)
      .then(d => (types.value = d))
  ])

  if (data.value) {
    indexer.value = new FacetIndexer(data.value.indexer)
    indexer.value.facets.Type.all = indexer.value.allKeys
  }
}

function onScroll(e) {
  if (
    lgAndUp.value &&
    filtersAutoHideEnabled.value &&
    filtersShown.value &&
    e.target.documentElement.scrollTop > window.innerHeight / 2
  )
    filtersShown.value = false
}

function resetFilters() {
  filtersData.value = Object.assign({}, FILTERS_VALUE_DEFAULT)
}

function toggleFilters() {
  debounced()
  filtersAutoHideEnabled.value = false
  filtersShown.value = !filtersShown.value
}

onMounted(async () => {
  watch(
    filtersData,
    newValue => {
      if (!debounced) {
        debounced = debounce(() => {
          filtersAutoHideEnabled.value = true
        }, 2000)
      } else if (debounced && container.value && lgAndUp.value) {
        debounced()
        filtersAutoHideEnabled.value = false

        scrollToRef(container, { behavior: 'instant' })
      }

      emit('update:filtersValue', newValue)
    },
    { deep: true }
  )
  watch(facetQuery, newValue => {
    if (indexer.value) {
      const result = indexer.value.query(newValue)
      facetCounts.value = result.counts()
      facetKeys.value = result.keys()
    }
  })

  await fetchData()

  filtersData.value = Object.assign(
    {},
    FILTERS_VALUE_DEFAULT,
    props.filtersValue
  )
})
</script>
